import { z } from 'zod'
import toast from 'react-hot-toast'
import dayjs, { type Dayjs } from 'dayjs'
import { PartnerPageData } from 'types/types'
import { ORG_TYPES } from 'types/affiliates'
import { PASSWORD_ERROR_MESSAGES } from './constants'

export const ValidEmail = z.string().email('Wrong email address!')

export const ValidNickname = z
  .string()
  .min(6, { message: 'Name must be at least 6 characters' })
  .max(20, { message: 'Name must be less than 20 characters' })

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, { message: PASSWORD_ERROR_MESSAGES.TOO_SHORT }),
})

export const buildRegisterSchema = (page: PartnerPageData) =>
  z
    .object({
      email: z.string().email(),
      nickname: page.feature_flags.sign_up_collect_first_and_last_name
        ? z.any().optional()
        : z.string().min(6).max(20),
      first_name: page.feature_flags.sign_up_collect_first_and_last_name
        ? z.string().min(3).max(20)
        : z.any().optional(),
      last_name: page.feature_flags.sign_up_collect_first_and_last_name
        ? z.string().min(3).max(20)
        : z.any().optional(),
      password: z
        .string()
        .regex(new RegExp('[0-9]'), { message: PASSWORD_ERROR_MESSAGES.NUMBER })
        .regex(new RegExp('(?=.*[a-z])(?=.*[A-Z])'), {
          message: PASSWORD_ERROR_MESSAGES.ONE_UPPERCASE_AND_LOWER,
        })
        .min(8, { message: PASSWORD_ERROR_MESSAGES.TOO_SHORT }),
      secondPassword: z
        .string()
        .regex(new RegExp('[0-9]'), { message: PASSWORD_ERROR_MESSAGES.NUMBER })
        .regex(new RegExp('(?=.*[a-z])(?=.*[A-Z])'), {
          message: PASSWORD_ERROR_MESSAGES.ONE_UPPERCASE_AND_LOWER,
        })
        .min(8, { message: PASSWORD_ERROR_MESSAGES.TOO_SHORT }),
      acceptTerms: z.literal<boolean>(true),
      additionalAnswer: z.boolean().optional(),
      dob: page.feature_flags.sign_up_dob
        ? z
            .custom<Dayjs>((val) => dayjs.isDayjs(val), { message: 'Invalid date' })
            .refine((dob) => {
              if (!dob) return false
              if (!page.feature_flags.eighteen_plus_entry) return true
              const today = dayjs()
              const age = today.diff(dob, 'year')
              return age >= 18
            }, 'You must be 18 or over to create an account')
        : z.any().optional(),
      nationality: page.feature_flags.sign_up_nationality ? z.string().min(3) : z.any().optional(),
      address: page.feature_flags.sign_up_address ? z.string().min(3) : z.any().optional(),
      country_of_residence: page.feature_flags.sign_up_country_of_residence
        ? z.string().min(2)
        : z.any().optional(),
      team_name: page.feature_flags.sign_up_team_name ? z.string().min(3) : z.any().optional(),
    })
    .refine((data) => data.password === data.secondPassword, {
      message: 'Passwords do not match',
      path: ['secondPassword'],
    })

export type RegisterFormValues = z.output<ReturnType<typeof buildRegisterSchema>>

export const registerFormDefaultValues = {
  password: '',
  nickname: '',
  secondPassword: '',
  dob: null,
  address: '',
  country_of_residence: '',
  team_name: '',
  nationality: '',
  acceptTerms: false,
  additionalAnswer: false,
}

export const PostalCodeValidation = z
  .string()
  .regex(
    new RegExp(
      '^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$',
    ),
    {
      message: 'Please enter a valid postcode',
    },
  )

export const buildJoinGameErrorMessages = ({
  canJoinGame,
  acceptTerms,
  tiebreakerAnswer,
  tiebreakerQuestion,
}: {
  canJoinGame: boolean
  acceptTerms: boolean
  tiebreakerAnswer: string
  tiebreakerQuestion: string | undefined
}) => {
  if (!canJoinGame) {
    toast.error('Please make sure you have made all your predictions for the current round.')
  }

  if (!acceptTerms) {
    toast.error('Please accept the terms and conditions to enter the game')
  }

  if (tiebreakerQuestion && !tiebreakerAnswer) {
    toast.error('Please make sure you have made your prediction for the tiebreaker.')
  }
}

export const buildSavePredictionsErrorMessages = ({
  canSavePredictions,
}: {
  canSavePredictions: boolean
}) => {
  if (!canSavePredictions) {
    toast.error(
      'You must make all your predictions for the round before you can save your predictions',
    )
  }
}

export const UserEmailPreferencesValidation = z.object({
  HOUR_BEFORE_DEADLINE: z.boolean(),
  GAME_FINISHED: z.boolean(),
  ROUND_FINISHED: z.boolean(),
})

const passwordSchema = z
  .string()
  .min(8, { message: PASSWORD_ERROR_MESSAGES.TOO_SHORT })
  .regex(/[0-9]/, { message: PASSWORD_ERROR_MESSAGES.NUMBER })
  .regex(/(?=.*[a-z])(?=.*[A-Z])/, {
    message: PASSWORD_ERROR_MESSAGES.ONE_UPPERCASE_AND_LOWER,
  })

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    repeatPassword: passwordSchema,
  })
  .refine((data) => data.password === data.repeatPassword, {
    message: "Passwords don't match",
    path: ['repeatPassword'],
  })

/** Adds `https://` to a non-empty website that was typed without a scheme. */
export const withHttps = (value: string) => {
  const trimmed = value.trim()
  return trimmed && !/^https?:\/\//i.test(trimmed) ? `https://${trimmed}` : trimmed
}

export const AffiliateOrgSchema = z.object({
  organisation_name: z
    .string()
    .trim()
    .min(2, { message: 'Organisation name must be at least 2 characters' })
    .max(150, { message: 'Organisation name must be 150 characters or less' }),
  org_type: z.enum(ORG_TYPES, { errorMap: () => ({ message: 'Choose an organisation type' }) }),
  city: z
    .string()
    .trim()
    .min(1, { message: 'Enter your town or city' })
    .max(100, { message: 'Town or city must be 100 characters or less' }),
  website: z
    .string()
    .transform(withHttps)
    .pipe(
      z
        .string()
        .max(255, { message: 'Website must be 255 characters or less' })
        .refine((value) => value === '' || /^https?:\/\/\S+\.\S+$/i.test(value), {
          message: 'Enter a valid website, e.g. example.com',
        }),
    ),
  notes: z.string().max(2000, { message: 'Notes must be 2000 characters or less' }),
})

export type AffiliateOrgFormValues = z.output<typeof AffiliateOrgSchema>

export const buildAffiliateSignupSchema = (isDobRequired: boolean) =>
  AffiliateOrgSchema.extend({
    dob: isDobRequired
      ? z
          .custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
            message: 'Enter your date of birth',
          })
          .refine((dob) => dayjs().diff(dob, 'year') >= 18, 'Partners must be 18 or over')
      : z.any().optional(),
    accepted_terms: z.literal(true, {
      errorMap: () => ({ message: 'You must accept the Partner Terms and Conditions to apply' }),
    }),
  })

export type AffiliateSignupFormValues = AffiliateOrgFormValues & {
  dob: Dayjs | null
  accepted_terms: boolean
}
